/**
 * 証明書と DKIM 鍵の横断一覧 `/certificates`（A149 / A317）。
 *
 * ドメイン詳細からは「そのドメインの分」しか見えないので、
 *   - 期限が近い証明書がプラットフォーム全体で何枚あるか
 *   - 発行に失敗し続けている証明書はどれか
 *   - ローテーション期限を過ぎた DKIM 鍵はどれか
 * が誰にも分からなかった。ここはその横断ビュー。
 *
 * **DKIM 秘密鍵の取り出しは fresh-auth + 理由必須**。鍵素材は REVOKE 済みで
 * SECURITY DEFINER 関数からしか読めず、取り出しは同期監査に残る。
 * 再認証が要求されたら API クライアント層（`<ReauthGate>`）が吸収するので、
 * この画面に個別の対応は書かない。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, KeyRound, RefreshCw, ShieldCheck } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  CursorPagination,
  DataTable,
  PageHeader,
  StatCard,
  StatusBadge,
  Textarea,
  formatCount,
  formatDateTime,
  type DataTableColumn,
} from '@mail-sv/ui';
import { labelOf } from '@mail-sv/shared';
import { api, request, OPS_PREFIX } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { daysUntil } from '../lib/format.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { usePermission } from '../stores/auth.js';
import { PageTabs } from '../components/page-tabs.js';
import { FormDialog, SecretRevealDialog } from '../components/dialogs.js';
import { Field } from '../components/form-fields.js';
import { ListEmpty, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type { CertificateEntry, DkimKeyEntry, DkimPrivateKeyReveal } from '../types/api.js';

const routeApi = getRouteApi(RID.certificates);

/** これ以内に切れる証明書は警告扱い（ACME の更新は 30 日前から動く）。 */
const EXPIRY_WARN_DAYS = 30;

export function CertificatesPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  return (
    <>
      <PageHeader
        title="証明書・DKIM"
        description="プラットフォーム全体の TLS 証明書と DKIM 鍵。期限切れと発行失敗をここで一括して見ます。"
      />

      <PageTabs
        value={search.tab}
        onChange={(tab) => void navigate({ search: (prev) => ({ ...prev, tab }) })}
        tabs={[
          { value: 'certificates', label: 'TLS 証明書', icon: ShieldCheck },
          { value: 'dkim', label: 'DKIM 鍵', icon: KeyRound },
        ]}
      />

      {search.tab === 'certificates' ? <CertificatesTab /> : null}
      {search.tab === 'dkim' ? <DkimTab /> : null}
    </>
  );
}

/* ----------------------------------------------------------- TLS 証明書 */

function CertificatesTab(): React.ReactElement {
  const canRenew = usePermission(OPS_PERMISSION.certWrite);
  // 再インストールはノードコマンドなので、証明書の権限とは別に `node.command` が要る。
  const canCommand = usePermission(OPS_PERMISSION.nodeCommand);

  const list = useCursorList<CertificateEntry>({
    path: '/certificates',
    keyFor: (params) => qk.certificates(params),
    filters: {},
  });

  const rows = list.rows;
  const expiringSoon = rows.filter((c) => {
    const d = daysUntil(c.notAfter);
    return d !== null && d <= EXPIRY_WARN_DAYS;
  }).length;
  const failing = rows.filter((c) => c.failureCount > 0).length;

  const columns: Array<DataTableColumn<CertificateEntry>> = [
    {
      id: 'commonName',
      header: 'コモンネーム',
      cell: (c) => (
        <div className="min-w-0 space-y-0.5">
          {c.domainId ? (
            <Link
              to="/domains/$domainId"
              params={{ domainId: c.domainId }}
              search={{ tab: 'cert' }}
              className="block truncate font-medium hover:text-primary"
            >
              {c.commonName}
            </Link>
          ) : (
            <span className="block truncate font-medium">{c.commonName}</span>
          )}
          {c.san.length > 1 ? (
            <span className="block truncate text-xs text-muted-foreground">
              SAN {c.san.length} 件
            </span>
          ) : null}
        </div>
      ),
    },
    { id: 'purpose', header: '用途', hideBelow: 'md', cell: (c) => labelOf(c.purpose) },
    { id: 'status', header: '状態', cell: (c) => <StatusBadge status={c.status} size="sm" dot /> },
    {
      id: 'notAfter',
      header: '有効期限',
      cell: (c) => <ExpiryCell notAfter={c.notAfter} />,
    },
    { id: 'issuer', header: '発行者', hideBelow: 'lg', cell: (c) => c.issuer ?? '—' },
    {
      id: 'node',
      header: 'ノード',
      hideBelow: 'lg',
      cell: (c) =>
        c.serverNodeId ? (
          <Link
            to="/nodes/$nodeId"
            params={{ nodeId: c.serverNodeId }}
            search={{ tab: 'overview', range: '24h' }}
            className="hover:text-primary"
          >
            {c.serverNodeId}
          </Link>
        ) : (
          '—'
        ),
    },
    {
      id: 'failure',
      header: '失敗',
      align: 'right',
      hideBelow: 'md',
      // 失敗が積み上がっている証明書は、放っておくと期限切れまで行く。
      cell: (c) =>
        c.failureCount > 0 ? (
          <Badge variant="destructive" size="sm" title={c.lastError ?? undefined}>
            {c.failureCount} 回
          </Badge>
        ) : (
          '—'
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-32',
      cell: (c) =>
        canRenew ? (
          <div className="flex items-center justify-end gap-1">
            <RenewButton certId={c.id} />
            <InstallButton cert={c} disabled={!canCommand} />
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label="表示中の証明書" value={formatCount(rows.length)} icon={ShieldCheck} />
        <StatCard
          label={`${EXPIRY_WARN_DAYS} 日以内に期限`}
          value={formatCount(expiringSoon)}
          tone={expiringSoon > 0 ? 'warning' : 'default'}
        />
        <StatCard
          label="発行に失敗中"
          value={formatCount(failing)}
          tone={failing > 0 ? 'destructive' : 'default'}
        />
      </div>

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} />
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(c) => c.id}
        loading={list.query.isLoading}
        empty={<ListEmpty filtered={false} entityLabel="証明書" />}
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={rows.length}
            loading={list.query.isFetching}
          />
        }
      />
    </div>
  );
}

function ExpiryCell({ notAfter }: { notAfter: string | null }): React.ReactElement {
  const days = daysUntil(notAfter);
  if (notAfter === null || days === null) return <span className="text-muted-foreground">—</span>;
  if (days < 0) {
    return (
      <Badge variant="destructive" size="sm">
        期限切れ
      </Badge>
    );
  }
  return (
    <span className={days <= EXPIRY_WARN_DAYS ? 'text-warning' : undefined}>
      {formatDateTime(notAfter)}
      <span className="ml-1 text-xs text-muted-foreground">（残り {days} 日）</span>
    </span>
  );
}

function RenewButton({ certId }: { certId: string }): React.ReactElement {
  const qc = useQueryClient();
  const renew = useMutation({
    mutationFn: () => api.post(`/certificates/${certId}/renew`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ops', 'certificates'] }),
  });
  return (
    <Button variant="outline" size="sm" onClick={() => renew.mutate()} loading={renew.isPending}>
      <RefreshCw aria-hidden />
      更新
    </Button>
  );
}

/**
 * 証明書の再インストール（R75）。
 *
 * `POST /ops/certificates/:id/renew` は **ACME で取り直す**操作で、
 * 「取れている証明書がノードに置かれていない / 置いた後 Postfix と Dovecot が
 * 読み直していない」は直せない。その復旧手段が `cert.install` のノードコマンドで、
 * API（`POST /ops/nodes/:nodeId/commands`）は R0 から在るのに**どの画面からも
 * 発行できなかった**（運営者は DB に直接行を入れるしかなかった）。
 *
 * 引数は `{ certId }`。**キーは `args`**（`payload` ではない。サーバーの zod は
 * `.strict()` なので `payload` で送ると 400 になる）。
 *
 * ノード未割当の証明書（`serverNodeId === null`）は宛先が無いので押させない。
 */
function InstallButton({
  cert,
  disabled,
}: {
  cert: CertificateEntry;
  disabled: boolean;
}): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const install = useMutation({
    mutationFn: () =>
      api.post(`/nodes/${cert.serverNodeId ?? ''}/commands`, {
        kind: 'cert.install',
        args: { certId: cert.id },
      }),
    onSuccess: () => setOpen(false),
  });

  const unassigned = cert.serverNodeId === null;

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        disabled={disabled || unassigned}
        title={
          unassigned
            ? 'この証明書はノードに割り当てられていません'
            : 'ノードへ配布し直して Postfix / Dovecot に読み直させます'
        }
        onClick={() => setOpen(true)}
      >
        <Download aria-hidden />
        <span className="sr-only">再インストール</span>
      </Button>
      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title="証明書を再インストール"
        description={`${cert.commonName} の現在の鍵と証明書をノードへ配布し直し、Postfix と Dovecot に読み直させます。証明書そのものは取り直しません（取り直しは「更新」）。`}
        submitLabel="再インストールする"
        pending={install.isPending}
        error={install.isError ? install.error : undefined}
        onSubmit={() => install.mutate()}
      >
        <dl className="space-y-1 rounded-lg border border-border bg-panel-elevated px-3 py-2 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">対象ノード</dt>
            <dd className="font-mono text-xs">{cert.serverNodeId}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">証明書 ID</dt>
            <dd className="font-mono text-xs">{cert.id}</dd>
          </div>
        </dl>
        <p className="text-xs text-muted-foreground">
          実行結果はノード詳細の「コマンド履歴」に残ります。
        </p>
      </FormDialog>
    </>
  );
}

/* -------------------------------------------------------------- DKIM */

function DkimTab(): React.ReactElement {
  const canReveal = usePermission(OPS_PERMISSION.secretRead);
  const [revealing, setRevealing] = React.useState<DkimKeyEntry | null>(null);

  const list = useCursorList<DkimKeyEntry>({
    path: '/dkim/keys',
    keyFor: (params) => qk.dkimKeys(params),
    filters: {},
  });

  const columns: Array<DataTableColumn<DkimKeyEntry>> = [
    {
      id: 'domain',
      header: 'ドメイン',
      cell: (k) => (
        <Link
          to="/domains/$domainId"
          params={{ domainId: k.domainId }}
          search={{ tab: 'dkim' }}
          className="font-medium hover:text-primary"
        >
          {k.domainName}
        </Link>
      ),
    },
    {
      id: 'selector',
      header: 'セレクタ',
      cell: (k) => <code className="font-mono text-sm">{k.selector}</code>,
    },
    { id: 'algorithm', header: 'アルゴリズム', hideBelow: 'md', cell: (k) => k.algorithm },
    { id: 'state', header: '状態', cell: (k) => <StatusBadge status={k.state} size="sm" dot /> },
    {
      id: 'publishedAt',
      header: 'DNS 公開',
      hideBelow: 'md',
      cell: (k) => formatDateTime(k.publishedAt),
    },
    {
      id: 'activatedAt',
      header: '署名開始',
      hideBelow: 'lg',
      cell: (k) => formatDateTime(k.activatedAt),
    },
    {
      id: 'rotateAfter',
      header: 'ローテ期限',
      cell: (k) => {
        const days = daysUntil(k.rotateAfter);
        if (k.rotateAfter === null || days === null) return '—';
        return (
          <span className={days < 0 ? 'text-destructive' : days <= 30 ? 'text-warning' : undefined}>
            {formatDateTime(k.rotateAfter)}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-32',
      cell: (k) =>
        canReveal ? (
          <Button variant="outline" size="sm" onClick={() => setRevealing(k)}>
            <KeyRound aria-hidden />
            秘密鍵
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-5">
      <Alert>
        <AlertDescription>
          秘密鍵の取り出しは<strong>復旧作業と鍵の移設のためだけ</strong>の操作です。
          理由が監査ログに残り、取り出した事実は消せません。
          <RefetchingDot active={list.query.isFetching && !list.query.isLoading} />
        </AlertDescription>
      </Alert>

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(k) => k.id}
        loading={list.query.isLoading}
        empty={<ListEmpty filtered={false} entityLabel="DKIM 鍵" />}
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      <RevealPrivateKeyDialog dkimKey={revealing} onClose={() => setRevealing(null)} />
    </div>
  );
}

function RevealPrivateKeyDialog({
  dkimKey,
  onClose,
}: {
  dkimKey: DkimKeyEntry | null;
  onClose: () => void;
}): React.ReactElement {
  const [reason, setReason] = React.useState('');
  const [revealed, setRevealed] = React.useState<DkimPrivateKeyReveal | null>(null);

  React.useEffect(() => {
    if (!dkimKey) return;
    setReason('');
    setRevealed(null);
  }, [dkimKey]);

  const submit = useMutation({
    mutationFn: () =>
      // **理由はクエリで渡す**（API は `GET` + `request.query` で受ける）。
      // body で送っても読まれないので 400 になる。
      request<DkimPrivateKeyReveal>(`${OPS_PREFIX}/dkim/keys/${dkimKey?.id ?? ''}/private`, {
        method: 'GET',
        query: { reason: reason.trim() },
        silent: false,
      }),
    onSuccess: (data) => setRevealed(data),
  });

  return (
    <>
      <FormDialog
        open={dkimKey !== null && revealed === null}
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        title="DKIM 秘密鍵を取り出す"
        description={dkimKey ? `${dkimKey.domainName} / セレクタ ${dkimKey.selector}` : undefined}
        submitLabel="取り出す"
        pending={submit.isPending}
        error={submit.error}
        disabled={reason.trim().length < 10}
        onSubmit={() => submit.mutate()}
      >
        <Alert variant="warning">
          <AlertDescription>
            この操作には直近の再認証が必要です。求められたら 2 要素目を入力してください。
          </AlertDescription>
        </Alert>
        <Field label="理由" required hint="10 文字以上。監査ログに残ります。">
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="ノード node-01 の再構築に伴う鍵の移設（INC-2026-0007）"
          />
        </Field>
      </FormDialog>

      <SecretRevealDialog
        open={revealed !== null}
        onOpenChange={(next) => {
          if (!next) {
            setRevealed(null);
            onClose();
          }
        }}
        title="DKIM 秘密鍵"
        items={
          revealed
            ? [
                { label: `セレクタ（version ${revealed.version}）`, value: revealed.selector },
                { label: '秘密鍵（PEM）', value: revealed.privateKeyPem },
              ]
            : []
        }
      />
    </>
  );
}
